<template>
    <b-row class="mb-3">
        <b-card no-body bg-variant="white" class="w-100">
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0">다국어 문자열 검색하기</h5>

                    <div v-if="listLoading">
                        <div class="d-flex align-items-center" style="height: 36px">
                            <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                            <p class="d-inline-block mb-0">조회중...</p>
                        </div>
                    </div>
                    <div v-else class="d-flex align-items-center">
                        <b-button form="locale-string-search-form"
                                  type="reset"
                                  class="mr-2"
                                  :disabled="listLoading"
                        >
                            {{ $t('초기화') }}
                        </b-button>
                        <b-button
                            variant="primary"
                            :disabled="listLoading"
                            form="locale-string-search-form"
                            type="submit"
                        >
                            {{ $t('검색하기') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <div class="p-3">
                <b-form id="locale-string-search-form"
                        autocomplete="new-password"
                        @submit.stop.prevent="searchData"
                        @reset.prevent="resetSearch"
                >
                    <b-row>
                        <b-col cols="12" md="6" class="mb-3 mb-xl-0">
                            <b-form-group
                                id="fieldset-input-search"
                                label="검색어"
                                label-for="input-search"
                                class="mb-0"
                            >
                                <b-form-input id="input-search"
                                              v-model="searchFormValue.search"
                                              :disabled="listLoading"
                                              placeholder="문자열 키 또는 텍스트 검색"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6">
                            <b-form-group
                                id="fieldset-input-locale"
                                label="로케일"
                                label-for="input-locale"
                                class="mb-0"
                            >
                                <b-form-select id="input-locale"
                                               v-model="searchFormValue.locale"
                                               :disabled="listLoading"
                                >
                                    <b-form-select-option v-for="data in localeOptions"
                                                          :key="`${data.text}-${data.value}`"
                                                          :value="data.value">{{ data.text }}
                                    </b-form-select-option>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-form>

            </div>
        </b-card>


        <b-row class="mt-3">
            <b-col cols="12">
                <b-card
                    :class="[{ 'shadow-sm': searchResults || (!listLoading && searchResults) }, 'list-container']"
                    :bg-variant="!searchResults ? 'light' : 'white'"
                    no-body
                >
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 class="mb-0">다국어 문자열 목록</h6>
                            </div>
                            <div class="d-flex align-items-center">
                                <b-button variant="outline-primary" size="md"
                                          @click="openConditionModal('add')">
                                    다국어 문자열 추가
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <div class="table-container">
                        <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title. 폭은 머리가 정한다 -->
                        <b-table
                            responsive
                            :items="searchResults"
                            :fields="tableColumns"
                            class="mb-0"
                            table-class="ds-table ds-table-fixed locale-string-table"
                            show-empty
                            :busy="listLoading"
                        >
                            <template #empty>
                                <PageEmptyComponent
                                    :text="$t('조회된 다국어 문자열이 없습니다.')"
                                    :loading="listLoading"
                                    :loadingText="$t('다국어 문자열 목록을 조회 중입니다.')"
                                    :error="listError"
                                    @retry="fetchData"
                                />
                            </template>
                            <!-- 메인 칸 = 상세 창(상세 화면이 따로 없다) -->
                            <template #cell(key)="row">
                                <button type="button" class="ds-detail-link ds-ellipsis text-monospace" :title="row.value"
                                        @click="openConditionModal('detail', row.item)">{{ row.value }}</button>
                            </template>
                            <template #cell(ko)="row">
                                <span v-if="row.value" class="ds-ellipsis" :title="row.value">{{ row.value }}</span>
                                <span v-else class="ds-sub">-</span>
                            </template>
                            <template #cell(en)="row">
                                <span v-if="row.value" class="ds-ellipsis" :title="row.value">{{ row.value }}</span>
                                <span v-else class="ds-sub">-</span>
                            </template>
                            <template #cell(ca)="row">
                                <span v-if="row.value" class="ds-ellipsis" :title="row.value">{{ row.value }}</span>
                                <span v-else class="ds-sub">-</span>
                            </template>
                            <template #cell(updated_at)="row">
                                {{ get_date(row.value) }}
                            </template>

                            <template #head(detail)="data">
                                <span class="sr-only">{{ data.label }}</span>
                            </template>
                            <template #cell(detail)="row">
                                <button type="button" class="ds-row-icon-btn"
                                        :aria-label="`${row.item.key} 문자열 상세`" title="문자열 상세"
                                        @click="openConditionModal('detail', row.item)">
                                    <AppIcon name="chevron-right"/>
                                </button>
                            </template>
                        </b-table>

                        <div class="pagination-container">
                            <b-pagination
                                ref="pagination"
                                v-model="pagination.currentPage"
                                :total-rows="pagination.totalRows"
                                :per-page="pagination.perPage"
                                first-number
                                last-number
                                :disabled="listLoading"
                                align="fill"
                                size="sm"
                                @change="(page)=>changeQuery({page})"
                            ></b-pagination>
                            <b-form-select
                                v-model="pagination.perPage"
                                class="ml-3 mb-3"
                                style="width: 100px"
                                size="sm"
                                :disabled="listLoading"
                                @change="(pageSize)=>changeQuery({pageSize})"
                            >
                                <b-form-select-option v-for="data in perPageOptions"
                                                      :key="`${data.text}-${data.value}`"
                                                      :value="data.value">{{ data.text }}
                                </b-form-select-option>
                            </b-form-select>

                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <LocaleStringModal ref="localeStringModal"
                        :detail-id="detailId"
                        @updateDetailData="updateDetailData"
                        @addData="initPage"
                        @deleteData="initPage"
        />

    </b-row>
</template>

<script>
import {listLocalizedStrings} from '@shared_utils/api/projectSchema';
import {
    getDefaultPagination,
    onTooltipShow
} from '@shared/components/admin/pages/Management/common-data';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import PaginationPageMixin from '@shared/mixin/pagination-page-mixins';
import {
    listColumns,
    localeOptions,
    searchFormValue
} from '@shared/components/admin/pages/Management/ProjectSchema/LocaleString/data';
import LocaleStringModal
    from '@shared/components/admin/pages/Management/ProjectSchema/LocaleString/LocaleStringModal.vue';

export default {
    name: 'ManageLocaleString',
    components: {
        LocaleStringModal,
        PageEmptyComponent,
    },
    mixins: [PaginationPageMixin({searchForm: searchFormValue, pagination: getDefaultPagination(1, 20)})],
    props: {
        currentTab: {
            type: Number,
            default: 0
        }
    },

    data() {
        return {
            tableColumns: listColumns,
            detailId: null,
            listError: '',
        };
    },
    computed: {
        localeOptions(){
            return localeOptions;
        }
    },
    watch: {
        '$route': {
            handler() {
                if (this.$route.query.tab === 'localeString') {
                    this.setSearchDataFormQuery();
                    this.fetchData();
                }
            },
            // 직접 URL/콘솔 고급 메뉴 진입은 route 변경이 없어 handler 가 안 돌므로 마운트 시에도 한 번 판정한다
            immediate: true
        }
    },
    methods: {
        onTooltipShow,
        async searchData() {
            this.pagination.currentPage = 1;
            const checkSameQuery = await this.changeQuery({});
            if (checkSameQuery) {
                await this.fetchData();
            }
        },
        initPage() {
            this.resetForm();
            this.setSearchDataFormQuery();
            this.fetchData();
        },
        async fetchData() {
            try {
                this.listLoading = true;
                this.listError = '';

                const form = {};
                if(this.searchFormValue.locale == null){
                    form.search = this.searchFormValue.search;
                }
                else {
                    form[this.searchFormValue.locale] = this.searchFormValue.search;
                }

                const res = await listLocalizedStrings({
                    params: {
                        ...form,
                        page: this.pagination.currentPage,
                        per_page: this.pagination.perPage,
                    },
                });

                if (res.data == null) {
                    this.searchResults = [];
                    this.pagination.totalRows = 0;
                    return;
                }
                const {localized_strings, total, current_page} = res.data;

                this.searchResults = localized_strings.map((item, index) => {
                    const data = {
                        ...item,
                        value: JSON.stringify(item.value),
                        // No. 는 표시 순서(페이지를 넘어 이어짐). 받을 때 응답 페이지 기준으로 정한다 —
                        // 화면의 페이지 값은 잠깐 비우거나, 새로고침 직후 페이지 버튼이 응답 전에 1 로 깎는다
                        index: this.pagination.perPage * ((Number(current_page) || 1) - 1) + index + 1,
                    };
                    return data;
                });

                this.pagination.totalRows = total;
                this.pagination.currentPage = null;
                setTimeout(() => {
                    this.pagination.currentPage = Number(current_page);
                });
            } catch (error) {
                // 실패를 '조회된 다국어 문자열이 없습니다'로 보이지 않게 이전 결과를 비우고 표 안에 오류를 띄운다
                this.searchResults = [];
                this.pagination.totalRows = 0;
                if (error?.response?.status === 401) return;
                this.listError = `다국어 문자열 목록을 불러오지 못했습니다. ${getErrorMessage(error, '')}`.trim();
                this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), '다국어 문자열 검색 도중 오류가 발생했습니다. 잠시후 재시도 해주세요.');
            } finally {
                this.listLoading = false;
            }
        },
        async openConditionModal(mode, item) {
            if (mode === 'add') {
                this.detailId = null;
                this.$refs.localeStringModal.changeMode(mode);
                this.$bvModal.show('locale-string-detail-modal');
                return;
            }

            console.log(item);

            this.detailId = item.key;
            this.$refs.localeStringModal.changeMode(mode);
            this.$bvModal.show('locale-string-detail-modal');

        },
        updateDetailData(updatedItem) {
            const idx = this.searchResults.findIndex((item) => {
                return item.key === updatedItem.key;
            });
            // 번호는 받을 때 정한 그대로 둔다(예전엔 길이 - idx 로 다시 매겨 수정한 행 번호가 틀어졌다)
            this.$set(this.searchResults, idx, {...updatedItem, index: this.searchResults[idx]?.index});
        }
    }
};
</script>

<style lang="scss">
/* 고정 열 합 516(No. 64 · 문자열 키 220 · 수정일시 168 · 상세 64) + 한국어 · 영어 · 중국어 ~148씩.
   1366(메뉴 펼침)에서 탭 안 목록 카드의 표 폭 약 1062 안에 들어가게 — 그보다 좁으면 가로 스크롤 */
.locale-string-table {
    min-width: 960px;
}
</style>
